// src/pages/Sotuvchilar.js
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./Sotuvchilar.css";
import api from "../api";
import { useSklad } from "../SkladContext";

/* === Decimal-safe helpers (text -> clean decimal -> number) === */
// "12,30" -> "12.30"; faqat raqam/nuqta/minus qoldiriladi; bitta nuqta
const normDecStr = (v) => {
  if (v == null) return "";
  let s = String(v).trim().replace(",", ".");
  s = s.replace(/[^\d.-]/g, "");
  if (s.includes("-")) s = (s.startsWith("-") ? "-" : "") + s.replace(/-/g, "");
  const i = s.indexOf(".");
  if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, "");
  if (["", "-", ".", "-."].includes(s)) return "";
  return s;
};
const toNum = (v) => {
  const n = Number(normDecStr(v));
  return Number.isFinite(n) ? n : 0;
};

/** Backend maydonlari XARITASI (backend serializer bilan mos) */
const MAP = {
  name: "ismi",
  phone: "telraqam1",
  phone2: "telraqam2",
  passportSeria: "pasportseriyasi",
  shop: "Dokonnomi",
  addr: "dokonjoylashuvi",
  tgChatId: "dokontelegramchatid",

  // Muhim: endi initial_status ishlatamiz (frontdan jo’natiladi)
  state: "initial_status",     // ‘qarzdor’ | ‘haqdor’ | ‘neytral’
  debt: "qarhaq",              // imzoli balans (+ haqdor / - qarzdor)
  dueDate: "next_due_date",    // eng yaqin nasiya muddati (YYYY-MM-DD)

  // read-only (serializer qaytarishi mumkin)
  serverStatus: "current_status",
  hasOverdue: "has_overdue",

  clientTarif: "mijoztarifi",

  // ImageField’lar
  passportFile: "pasport_rasmi",
  clientFile:   "mijoz_rasmi",

  // Serializer’dan keladigan URL’lar
  passportUrl: "pasport_rasmi_url",
  clientUrl:   "mijoz_rasmi_url",

  // Sklad
  allowedSklads: "allowed_sklads",
  allowedSkladsDetail: "allowed_sklads_detail",
};

const ENDPOINT = "sotuvchi/";
const PAGE_SIZE = 40;

const money = (n) => `${Number(n || 0).toLocaleString("uz-UZ")} so'm`;
const cx = (...a) => a.filter(Boolean).join(" ");
const asArr = (res) => (Array.isArray(res?.data) ? res.data : res?.data?.results || []);
const hasNext = (res) => Boolean(res?.data?.next);

const todayStr = () => new Date().toISOString().slice(0, 10);
const normalizeBool = (v) =>
  v === true || v === 1 || v === "1" || (typeof v === "string" && v.toLowerCase() === "true");
const normalizeDate = (v) => {
  if (!v) return "";
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  const m = s.match(/^(\d{2})[/.-](\d{2})[/.-](\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : "";
};

/** UI statuslari */
const UI_STATES = {
  OVERDUE: "overdue_debtor",
  NOT_DUE: "debtor_not_due",
  NEUTRAL: "neutral",
  CREDITOR: "creditor",
};
const STATE_LABELS = {
  [UI_STATES.OVERDUE]: "Muddati o‘tgan qarzdor",
  [UI_STATES.NOT_DUE]: "Muddat kelmagan qarzdor",
  [UI_STATES.NEUTRAL]: "Neytral",
  [UI_STATES.CREDITOR]: "Haqdor",
};
const BADGE_CLASS = {
  [UI_STATES.OVERDUE]: "red",
  [UI_STATES.NOT_DUE]: "yellow",
  [UI_STATES.NEUTRAL]: "gray",
  [UI_STATES.CREDITOR]: "green",
};

/** server current_status -> UI map (agar bo‘lsa) */
const SERVER2UI_STATUS = (s) => {
  switch (s) {
    case "overdue_debtor": return UI_STATES.OVERDUE;
    case "debtor_not_due": return UI_STATES.NOT_DUE;
    case "neutral":        return UI_STATES.NEUTRAL;
    case "creditor":       return UI_STATES.CREDITOR;
    default:               return null;
  }
};

/** initial_status (backendga jo‘natiladigan) <- UI holati */
const UI2INITIAL = (ui) => {
  if (ui === UI_STATES.CREDITOR) return "haqdor";
  if (ui === UI_STATES.NEUTRAL)  return "neytral";
  return "qarzdor"; // qarzdorning har ikki turi
};

/** UI statusni hisoblash (<= bugun bo'lsa overdue) */
const computeUiStatus = ({ signedDebt, hasOverdue, dueDate }) => {
  const bal = Number(signedDebt || 0);
  const due = normalizeDate(dueDate);
  if (bal < 0) {
    if (normalizeBool(hasOverdue)) return UI_STATES.OVERDUE;
    if (due && due <= todayStr()) return UI_STATES.OVERDUE; // bugun ham qizil!
    return UI_STATES.NOT_DUE;
  }
  if (bal > 0) return UI_STATES.CREDITOR;
  return UI_STATES.NEUTRAL;
};

/** Formdagi holatga ko‘ra imzoli balans tayyorlash (DECIMAL-SAFE) */
const signDebtForSubmit = (uiState, val) => {
  const n = Math.abs(toNum(val));        // <-- text'dan toza number
  if (uiState === UI_STATES.CREDITOR) return +n;   // haqdor
  if (uiState === UI_STATES.NEUTRAL)  return 0;    // neytral
  return -n;                                       // qarzdor turlari
};

/* ========= Karta komponenti ========= */
const Card = React.memo(function Card({ it, onEdit, onRemove, onOpenImg }) {
  const label = STATE_LABELS[it.uiState] || "-";
  const badgeCls = BADGE_CLASS[it.uiState] || "gray";
  const showDue = it.uiState === UI_STATES.OVERDUE || it.uiState === UI_STATES.NOT_DUE;

  return (
    <div className="card fr-card" style={{ contain: "content" }}>
      <div className="card-inner">
        <div className="c-left">
          <div><b>Ismi:</b> {it.name}</div>
          <div>{it.phone}</div>
          <div>{it.phone2}</div>
          <div>{it.passport}</div>
          <div>{it.shop}</div>
          <div><b>Tarif:</b> {it.clientTarif || "-"}</div>
          <div><b>Holat:</b> {label}</div>
          {showDue && <div><b>Muddat:</b> {it.dueDate || "-"}</div>}
          <div className={cx("badge", badgeCls)}>{money(Math.abs(it.debt))}</div>
          {it.allowedSklads?.length > 0 && (
            <div><b>Sklad:</b> {it.allowedSklads.map(s => s.nom).join(", ")}</div>
          )}
        </div>

        <div className="img-col">
          <div
            className="img-box"
            onClick={() => it.passportImg && onOpenImg(it.passportImg, "Pasport rasmi")}
            title="Kattalashtirib ko‘rish"
          >
            {it.passportImg ? <img src={it.passportImg} alt="" loading="lazy" decoding="async" /> : <span className="ph">Pasport</span>}
          </div>
          <button className="mini" onClick={() => onEdit(it)}>O‘zgartirish</button>
        </div>

        <div className="img-col">
          <div
            className="img-box"
            onClick={() => it.clientImg && onOpenImg(it.clientImg, "Mijoz rasmi")}
            title="Kattalashtirib ko‘rish"
          >
            {it.clientImg ? <img src={it.clientImg} alt="" loading="lazy" decoding="async" /> : <span className="ph">Mijoz</span>}
          </div>
          <button className="mini" onClick={() => onEdit(it)}>O‘zgartirish</button>
        </div>
      </div>

      <div className="card-actions">
        <button className="chip chip-yellow" onClick={() => onEdit(it)}>Tahrirlash</button>
        <button className="chip chip-red" onClick={() => onRemove(it.id)}>O‘chirish</button>
      </div>
    </div>
  );
});

/* ========= Sahifa ========= */
export default function CustomersPage() {
  // ro‘yhat + qidiruv (debounce bilan)
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  // narx turlari (mijoz tarifi uchun)
  const [priceTypes, setPriceTypes] = useState([]);

  // global sklad filter
  const { activeSklad } = useSklad();

  // sklad ro'yxati (sklad biriktiruv uchun)
  const [sklads, setSklads] = useState([]);

  // pagination by next-url (404 yo'qoladi)
  const [nextUrl, setNextUrl] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const loaderRef = useRef(null);

  // form/edit
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: "", phone: "", phone2: "",
    passportSeria: "", shop: "", addr: "",
    state: "", debt: "", tgChatId: "",
    clientTarif: "",
    dueDate: "",                  // qarzdor holatida ixtiyoriy
    passportImg: "", clientImg: "",
    passportFile: null, clientFile: null,
    allowedSklads: [],            // sklad ID'lari ro'yxati
  });

  // upload refs
  const passRef = useRef(null);
  const clientRef = useRef(null);

  // lightbox
  const [viewer, setViewer] = useState({ open: false, src: "", title: "" });
  const onOpenImg = (src, title) => setViewer({ open: true, src, title });

  /** Serverdan kelgan bitta elementni UI formatga o'tkazish */
  const fromServer = useCallback((it) => {
    const signedDebt = Number(it[MAP.debt] ?? 0);
    const dueDate = normalizeDate(it[MAP.dueDate]);
    const hasOverdue = normalizeBool(it[MAP.hasOverdue]);
    const srvStatus = SERVER2UI_STATUS(it[MAP.serverStatus]);
    const uiState = srvStatus || computeUiStatus({ signedDebt, hasOverdue, dueDate });

    return {
      id: it.id,
      name: it[MAP.name] || "",
      phone: it[MAP.phone] || "",
      phone2: it[MAP.phone2] || "",
      passportSeria: it[MAP.passportSeria] || "",
      passport: it[MAP.passportSeria] ? `P.S.R: ${it[MAP.passportSeria]}` : "",
      shop: it[MAP.shop] ? `Do’kon nomi: ${it[MAP.shop]}` : "",
      addr: it[MAP.addr] || "",
      uiState,
      debt: signedDebt,           // imzoli
      dueDate,
      hasOverdue,
      clientTarif: it[MAP.clientTarif] || "",
      passportImg: it[MAP.passportUrl] || "",
      clientImg:   it[MAP.clientUrl] || "",
      allowedSklads: it[MAP.allowedSkladsDetail] || [],
      __raw: it,
    };
  }, []);

  /** Narx turlari + Sklad ro'yxati */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("narx-turi/");
        setPriceTypes(Array.isArray(data) ? data : (data.results || []));
      } catch (e) {
        console.log("Narx turlari yuklash xato:", e?.response?.status, e?.response?.data);
      }
    })();
    (async () => {
      try {
        const { data } = await api.get("sklad/");
        setSklads(Array.isArray(data) ? data : (data.results || []));
      } catch (e) {
        console.log("Sklad ro'yxat yuklash xato:", e?.response?.status, e?.response?.data);
      }
    })();
  }, []);

  /** 1-sahifadan yuklash (qidiruv o‘zgarsa) */
  useEffect(() => {
    let cancelled = false;
    const loadFirst = async () => {
      setLoading(true);
      setHasMore(false);
      setNextUrl(null);
      try {
        const params = new URLSearchParams();
        params.set("page", "1");
        params.set("page_size", String(PAGE_SIZE));
        if (debouncedQ) params.set("search", debouncedQ);
        if (activeSklad && activeSklad !== "all") params.set("sklad", activeSklad);
        const res = await api.get(`${ENDPOINT}?${params.toString()}`);
        if (cancelled) return;
        const arr = asArr(res).map(fromServer);
        setList(arr);
        setHasMore(hasNext(res));
        setNextUrl(res?.data?.next || null);
      } catch (e) {
        console.log("LOAD FIRST ERR:", e?.response?.status, e?.response?.data);
        if (!cancelled) {
          setList([]);
          setHasMore(false);
          setNextUrl(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadFirst();
    return () => { cancelled = true; };
  }, [debouncedQ, fromServer, activeSklad]);

  /** Keyingi sahifalarni yuklash (next-url orqali) */
  const fetchMore = useCallback(async () => {
    if (!nextUrl) return;
    try {
      const res = await api.get(nextUrl);
      const arr = asArr(res).map(fromServer);
      setList((prev) => [...prev, ...arr]);
      setHasMore(hasNext(res));
      setNextUrl(res?.data?.next || null);
    } catch (e) {
      if (e?.response?.status === 404) { // out-of-range
        setHasMore(false);
        setNextUrl(null);
        return;
      }
      console.log("PAGING ERR:", e?.response?.status, e?.response?.data);
    }
  }, [nextUrl, fromServer]);

  /** Infinite scroll sentinel */
  useEffect(() => {
    if (!loaderRef.current) return;
    const io = new IntersectionObserver(async ([entry]) => {
      if (!entry.isIntersecting) return;
      if (!hasMore || loading || !nextUrl) return;
      setLoading(true);
      await fetchMore().finally(() => setLoading(false));
    }, { rootMargin: "300px" });
    io.observe(loaderRef.current);
    return () => io.disconnect();
  }, [loaderRef, hasMore, loading, nextUrl, fetchMore]);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => {
    setEditingId(null);
    setForm({
      name: "", phone: "", phone2: "",
      passportSeria: "", shop: "", addr: "",
      state: "", debt: "", tgChatId: "",
      clientTarif: "",
      dueDate: "",
      passportImg: "", clientImg: "",
      passportFile: null, clientFile: null,
      allowedSklads: [],
    });
  };

  // createObjectURL’larni tozalash
  const lastUrlsRef = useRef({ passportImg: null, clientImg: null });
  const revokeIfNeeded = (key) => {
    const u = lastUrlsRef.current[key];
    if (u && u.startsWith("blob:")) {
      URL.revokeObjectURL(u);
      lastUrlsRef.current[key] = null;
    }
  };
  useEffect(() => {
    return () => {
      revokeIfNeeded("passportImg");
      revokeIfNeeded("clientImg");
    };
  }, []);

  const handleFile = (e, key) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const url = URL.createObjectURL(f);
    if (key === "passport") {
      revokeIfNeeded("passportImg");
      lastUrlsRef.current.passportImg = url;
      setForm((prev) => ({ ...prev, passportImg: url, passportFile: f }));
    } else {
      revokeIfNeeded("clientImg");
      lastUrlsRef.current.clientImg = url;
      setForm((prev) => ({ ...prev, clientImg: url, clientFile: f }));
    }
  };

  const toFormData = (f, forUpdate) => {
    const fd = new FormData();
    // majburiylar
    fd.append(MAP.name, f.name);
    fd.append(MAP.phone, f.phone);
    fd.append(MAP.tgChatId, f.tgChatId);

    // balansni holatga qarab imzolaymiz (decimal-safe)
    if (f.state) {
      const signed = signDebtForSubmit(f.state, f.debt);
      fd.append(MAP.debt, String(signed));         // toza decimal string/number
      fd.append(MAP.state, UI2INITIAL(f.state));   // create/update ikkalasida xavfsiz
    }

    // ixtiyoriylar
    if (f.phone2)        fd.append(MAP.phone2, f.phone2);
    if (f.passportSeria) fd.append(MAP.passportSeria, f.passportSeria);
    if (f.shop)          fd.append(MAP.shop, f.shop);
    if (f.addr)          fd.append(MAP.addr, f.addr);
    if (f.clientTarif)   fd.append(MAP.clientTarif, f.clientTarif);

    // qarzdor turlarida muddat
    const isDebtorUI = f.state === UI_STATES.OVERDUE || f.state === UI_STATES.NOT_DUE;
    if (isDebtorUI && f.dueDate) {
      fd.append(MAP.dueDate, normalizeDate(f.dueDate));
    }

    if (f.passportFile instanceof File) fd.append(MAP.passportFile, f.passportFile);
    if (f.clientFile   instanceof File) fd.append(MAP.clientFile,   f.clientFile);

    // Sklad biriktiruv (har bir ID alohida append qilinadi)
    if (Array.isArray(f.allowedSklads)) {
      f.allowedSklads.forEach(id => fd.append(MAP.allowedSklads, id));
    }
    return fd;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.tgChatId) {
      alert("Ismi, telefon va Telegram chat_id majburiy!");
      return;
    }
    // foydalanuvchi xatosini oldini olish
    if (form.state === UI_STATES.OVERDUE) {
      const d = normalizeDate(form.dueDate);
      if (!d) { alert("Muddati o‘tgan uchun sana tanlang."); return; }
      if (d > todayStr()) { alert("Muddati o‘tgan bo‘lishi uchun sana bugun yoki undan oldin bo‘lishi kerak."); return; }
    }
    try {
      const fd = toFormData(form, Boolean(editingId));
      if (editingId) {
        await api.patch(`${ENDPOINT}${editingId}/`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await api.post(ENDPOINT, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }

      // ro‘yxatni 1-sahifadan qayta yuklaymiz
      reset();
      setLoading(true);
      setHasMore(false);
      setNextUrl(null);
      const params = new URLSearchParams();
      params.set("page", "1");
      params.set("page_size", String(PAGE_SIZE));
      if (debouncedQ) params.set("search", debouncedQ);
      const res = await api.get(`${ENDPOINT}?${params.toString()}`);
      setList(asArr(res).map(fromServer));
      setHasMore(hasNext(res));
      setNextUrl(res?.data?.next || null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      console.log("SAVE ERR:", e2?.response?.status, e2?.response?.data);
      alert(`Saqlashda xato: ${JSON.stringify(e2?.response?.data || e2.message)}`);
    } finally {
      setLoading(false);
    }
  };

  const edit = async (it) => {
    try {
      const { data } = await api.get(`${ENDPOINT}${it.id}/`);
      const signedDebt = Number(data[MAP.debt] ?? 0);
      const dueDate = normalizeDate(data[MAP.dueDate]);
      const srvStatus = SERVER2UI_STATUS(data[MAP.serverStatus]);
      const hasOverdue = normalizeBool(data[MAP.hasOverdue]);
      const uiState = srvStatus || computeUiStatus({ signedDebt, hasOverdue, dueDate });

      setEditingId(it.id);
      const existingSklads = (data[MAP.allowedSklads] || []).map(id => Number(id));
      setForm({
        name: data[MAP.name] || "",
        phone: data[MAP.phone] || "",
        phone2: data[MAP.phone2] || "",
        passportSeria: data[MAP.passportSeria] || "",
        shop: data[MAP.shop] || "",
        addr: data[MAP.addr] || "",
        state: uiState,
        debt: String(Math.abs(signedDebt)), // UI doim musbat ko’rsatadi
        tgChatId: data[MAP.tgChatId] || "",
        clientTarif: data[MAP.clientTarif] || "",
        dueDate: dueDate || "",
        passportImg: data[MAP.passportUrl] || "",
        clientImg:   data[MAP.clientUrl] || "",
        passportFile: null,
        clientFile: null,
        allowedSklads: existingSklads,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      console.log("EDIT LOAD ERR:", e?.response?.status, e?.response?.data);
      alert("Ma'lumotlarni olishda xato.");
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm("O‘chirishni tasdiqlaysizmi?")) return;
    try {
      await api.delete(`${ENDPOINT}${id}/`);
      setList((a) => a.filter((x) => x.id !== id));
    } catch (e) {
      console.log("DELETE ERR:", e?.response?.status, e?.response?.data);
      alert(e?.response?.data?.detail || "O‘chirishda xato.");
    }
  };

  // Kartalarga serverdan kelgan ro'yxatni chiqaramiz
  const items = useMemo(() => list, [list]);

  return (
    <div className="cust-wrap">
      <h2 className="page-title">Mijoz qo‘shish</h2>

      {/* FORM */}
      <form className="form-grid fr-panel" onSubmit={submit}>
        <input className="input" placeholder="Ismi" value={form.name} onChange={(e)=>onChange("name", e.target.value)} required />
        <input className="input" placeholder="Telefon raqami" value={form.phone} onChange={(e)=>onChange("phone", e.target.value)} required />
        <input className="input" placeholder="Qo‘shimcha telefon raqam" value={form.phone2} onChange={(e)=>onChange("phone2", e.target.value)} />

        <input className="input" placeholder="Passport Seriyasi va raqami" value={form.passportSeria} onChange={(e)=>onChange("passportSeria", e.target.value)} />
        <input className="input" placeholder="Do‘kon nomi" value={form.shop} onChange={(e)=>onChange("shop", e.target.value)} />
        <input className="input" placeholder="Do‘kon joylashuvi" value={form.addr} onChange={(e)=>onChange("addr", e.target.value)} />

        {/* Holati (4 ta variant) */}
        <div className="select-wrap">
          <select className="input" value={form.state} onChange={(e)=>onChange("state", e.target.value)}>
            <option value="">Holati (tanlang)</option>
            <option value={UI_STATES.OVERDUE}>Muddati o‘tgan qarzdor</option>
            <option value={UI_STATES.NOT_DUE}>Muddat kelmagan qarzdor</option>
            <option value={UI_STATES.NEUTRAL}>Neytral</option>
            <option value={UI_STATES.CREDITOR}>Haqdor</option>
          </select>
          <span className="chev">▾</span>
        </div>

        {/* DECIMAL TEXT INPUT */}
        <input
          className="input"
          type="text"
          inputMode="decimal"
          placeholder="Summa (so'm) — musbat kiriting"
          value={form.debt}
          onChange={(e)=>onChange("debt", normDecStr(e.target.value))}
          onBlur={(e)=>onChange("debt", normDecStr(e.target.value))}
        />

        {/* Muddat (faqat qarzdor turlari uchun ixtiyoriy) */}
        <input
          className="input"
          type="date"
          value={form.dueDate}
          onChange={(e)=>onChange("dueDate", e.target.value)}
          disabled={!(form.state === UI_STATES.OVERDUE || form.state === UI_STATES.NOT_DUE)}
          title="Qarzdor holatida belgilash mumkin"
        />

        {/* Mijoz tarifi */}
        <div className="select-wrap">
          <select className="input" value={form.clientTarif} onChange={(e)=>onChange("clientTarif", e.target.value)}>
            <option value="">Mijoz tarifi</option>
            {priceTypes.map(t => (
              <option key={t.id} value={t.nom}>{t.nom}</option>
            ))}
          </select>
          <span className="chev">▾</span>
        </div>

        <input className="input" placeholder="Telegram guruh chat_id" value={form.tgChatId} onChange={(e)=>onChange("tgChatId", e.target.value)} required />

        {/* Sklad biriktiruv */}
        {sklads.length > 0 && (
          <div className="sklad-assign">
            <div className="sklad-assign-label">Sklad biriktiruv <span className="muted">(bo'sh = barcha skladdarga ko'rinadi)</span></div>
            <div className="sklad-assign-checks">
              {sklads.map(s => {
                const checked = form.allowedSklads.includes(s.id);
                return (
                  <label key={s.id} className="sklad-check-label">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        onChange("allowedSklads",
                          checked
                            ? form.allowedSklads.filter(id => id !== s.id)
                            : [...form.allowedSklads, s.id]
                        );
                      }}
                    />
                    {s.nom}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Uploadlar */}
        <div className="upload">
          <button type="button" className="btn" onClick={()=>passRef.current?.click()}>Fayldan yuklash</button>
          <span className="muted">Pasport rasmi</span>
          <input ref={passRef} hidden type="file" accept="image/*" onChange={(e)=>handleFile(e,"passport")} />
          {form.passportImg && <span className="ok">Yuklandi</span>}
        </div>

        <div className="upload">
          <button type="button" className="btn" onClick={()=>clientRef.current?.click()}>Fayldan yuklash</button>
          <span className="muted">Mijoz rasmi</span>
          <input ref={clientRef} hidden type="file" accept="image/*" onChange={(e)=>handleFile(e,"client")} />
          {form.clientImg && <span className="ok">Yuklandi</span>}
        </div>

        <button className="btn btn-green submit" type="submit">
          {editingId ? "Saqlash" : "Qo‘shish"}
        </button>
      </form>

      {/* Qidiruv (server) */}
      <div className="search-row">
        <input
          className="input search"
          placeholder="Mijoz ismi yoki telefon raqami bilan qidiring"
          value={q}
          onChange={(e)=>setQ(e.target.value)}
        />
        <button className="search-ico" type="button" aria-label="Qidirish">🔍</button>
      </div>

      {/* Kartalar */}
      <div className="cards-panel fr-panel">
        <div className="cards-grid">
          {items.map((it)=>(
            <Card
              key={it.id}
              it={it}
              onEdit={edit}
              onRemove={removeItem}
              onOpenImg={onOpenImg}
            />
          ))}
          {!items.length && !loading && <div className="empty">Hech narsa topilmadi</div>}
          {loading && <div className="empty small">Yuklanmoqda…</div>}
          {/* infinite scroll sentinel */}
          <div ref={loaderRef} style={{ height: 1 }} />
        </div>
      </div>

      {/* Lightbox */}
      {viewer.open && (
        <div className="lightbox" onClick={()=>setViewer({open:false, src:"", title:""})}>
          <div className="lightbox-inner" onClick={(e)=>e.stopPropagation()}>
            <div className="lb-head">
              <div className="lb-title">{viewer.title}</div>
              <button className="btn" onClick={()=>setViewer({open:false, src:"", title:""})}>Yopish</button>
            </div>
            <div className="lb-body">
              <img src={viewer.src} alt="" />
            </div>
            <div className="lb-foot">
              <a className="btn btn-green" href={viewer.src} download>Yuklab olish</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
